import { Button, IconButton, Tooltip, Typography } from '@material-ui/core'
import { SaveButton } from 'components/Common/Button/SaveButton'
import { MoneyPerQuantityInput } from 'components/Common/Input/MoneyPerQuantityInput'
import { QuantityField } from 'components/Common/UnitEditor/QuantityField'
import ValueLabel from 'components/Common/ValueLabel/ValueLabel'
import { WorkingStepIconSvg } from 'components/Common/WorkingSteps/WorkingStepIcon'
import { useLocalizedWorkingStepLabel } from 'components/Localization/useLocalizedWorkingStepLabel'
import { useArticleIdFilter } from 'features/BillOfMaterials/BoMItemFilter/Components/ArticleIdFilter/useArticleIdFilter'
import { useResourceIdFilter } from 'features/BillOfMaterials/BoMItemFilter/Components/WorkingStepsFilter/useResourceIdFilter'
import { MoneyString } from 'model/Money'
import { QuantityString, TimeString } from 'model/Quantity'
import { useTranslation } from 'react-i18next'
import { PriceSummaryDto } from 'services/APIs/InternalAPI/internal-api.contracts'
import { ReactComponent as FilterAltOffOutlined } from '_icons/filterAltOffOutlined.svg'
import { ReactComponent as FilterAltOutlined } from '_icons/filterAltOutlined.svg'
import { useProjectPriceSummaryDetailsStyles } from '../ProjectFinancialDetails.styles'
import { HomePriceButtonOutlined } from './HomePriceButtonOutlined'
import { useProjectPriceSummaryDetails } from './useProjectPriceSummaryDetails'

const getLoadingKey = (operation: string, priceSummary: PriceSummaryDto) => {
  return `${operation}-${priceSummary.workingStep.resource.id}-${priceSummary.workingStep.primaryWorkingStep}-${priceSummary.workingStep.secondaryWorkingStep}`
}

const EditableFields = ({
  changedPriceSummary,
  handleChange,
  handleResetToDefaults,
  isDiscountRatioDisabled,
  isDiscountValueDisabled,
  isSurchargeRatioDisabled,
  isSurchargeValueDisabled,
  validationErrors,
  loading,
}: {
  changedPriceSummary: PriceSummaryDto
  handleChange
  handleResetToDefaults: (
    valueType: 'discount' | 'surcharge',
    priceSummary: PriceSummaryDto
  ) => Promise<void>
  isDiscountRatioDisabled: boolean
  isDiscountValueDisabled: boolean
  isSurchargeRatioDisabled: boolean
  isSurchargeValueDisabled: boolean
  validationErrors: Record<string, string>
  loading: Record<string, boolean>
}) => {
  const { t } = useTranslation()

  if (!changedPriceSummary) {
    return null
  }

  return (
    <>
      <QuantityField
        value={changedPriceSummary.surchargeRatio}
        name="surchargeRatio"
        disabled={isSurchargeRatioDisabled}
        error={Boolean(validationErrors['surchargeRatio'])}
        // helperText={validationErrors['surchargeRatio']}
        style={{
          gridArea: 'surchp',
        }}
        label={
          <span>
            {t('common:surcharge')}
            <HomePriceButtonOutlined
              handleResetToDefaults={() =>
                handleResetToDefaults('surcharge', changedPriceSummary)
              }
              isLoading={
                loading[getLoadingKey('delete-surcharge', changedPriceSummary)]
              }
              isManuallySet={changedPriceSummary.isSurchargeManuallySet}
            />
          </span>
        }
        onChange={(value) => handleChange('surchargeRatio', value)}
        textAlign="right"
      />
      <QuantityField
        value={changedPriceSummary.surchargeValue}
        name="surchargeValue"
        disabled={isSurchargeValueDisabled}
        error={Boolean(validationErrors['surchargeValue'])}
        // helperText={validationErrors['surchargeValue']}
        style={{
          gridArea: 'surchv',
        }}
        label={
          <span>
            {t('common:surcharge')}
            <HomePriceButtonOutlined
              handleResetToDefaults={() =>
                handleResetToDefaults('surcharge', changedPriceSummary)
              }
              isLoading={
                loading[getLoadingKey('delete-surcharge', changedPriceSummary)]
              }
              isManuallySet={changedPriceSummary.isSurchargeManuallySet}
            />
          </span>
        }
        onChange={(value) => handleChange('surchargeValue', value)}
        textAlign="right"
      />

      <QuantityField
        value={changedPriceSummary.discountRatio}
        name="discountRatio"
        showMinus
        disabled={isDiscountRatioDisabled}
        error={Boolean(validationErrors['discountRatio'])}
        style={{
          gridArea: 'discp',
        }}
        // helperText={validationErrors['discountRatio']}
        label={
          <span>
            {t('common:discount')}
            <HomePriceButtonOutlined
              handleResetToDefaults={() =>
                handleResetToDefaults('discount', changedPriceSummary)
              }
              isLoading={
                loading[getLoadingKey('delete-discount', changedPriceSummary)]
              }
              isManuallySet={changedPriceSummary.isDiscountManuallySet}
            />
          </span>
        }
        onChange={(value) => handleChange('discountRatio', value)}
        textAlign="right"
      />
      <QuantityField
        style={{
          gridArea: 'discv',
        }}
        value={changedPriceSummary.discountValue}
        name="discountValue"
        disabled={isDiscountValueDisabled}
        allowNegative={false}
        showMinus
        error={Boolean(validationErrors['discountValue'])}
        // helperText={validationErrors['discountValue']}
        label={
          <span>
            {t('common:discount')}
            <HomePriceButtonOutlined
              handleResetToDefaults={() =>
                handleResetToDefaults('discount', changedPriceSummary)
              }
              isLoading={
                loading[getLoadingKey('delete-discount', changedPriceSummary)]
              }
              isManuallySet={changedPriceSummary.isDiscountManuallySet}
            />
          </span>
        }
        onChange={(value) => handleChange('discountValue', value)}
        textAlign="right"
      />
    </>
  )
}

const ReadOnlyFields = ({
  changedPriceSummary,
}: {
  changedPriceSummary: PriceSummaryDto
}) => {
  const { t } = useTranslation()

  if (!changedPriceSummary) {
    return null
  }

  return (
    <>
      <ValueLabel
        value={QuantityString(changedPriceSummary.surchargeRatio)}
        label={t('common:surcharge')}
        valueLabelRootStyles={{
          whiteSpace: 'pre-line',
          gridArea: 'surchp',
        }}
      />
      <ValueLabel
        value={MoneyString(changedPriceSummary.surchargeValue)}
        label={t('common:surcharge')}
        valueLabelRootStyles={{
          whiteSpace: 'pre-line',
          gridArea: 'surchv',
        }}
      />

      <ValueLabel
        value={QuantityString(changedPriceSummary.discountRatio)}
        label={t('common:discount')}
        valueLabelRootStyles={{
          whiteSpace: 'pre-line',
          gridArea: 'discp',
        }}
      />
      <ValueLabel
        value={MoneyString(changedPriceSummary.discountValue)}
        label={t('common:discount')}
        valueLabelRootStyles={{
          whiteSpace: 'pre-line',
          gridArea: 'discv',
        }}
      />
    </>
  )
}

export const ProjectWorkingStepPriceSummary = (props: {
  priceSummary: PriceSummaryDto
}) => {
  const classes = useProjectPriceSummaryDetailsStyles()
  const { getLocalizedWorkingStep } = useLocalizedWorkingStepLabel()
  const { t } = useTranslation()

  const {
    changedPriceSummary,
    handleChange,
    handleSubmit,
    changed,
    handleReset,
    saving,
    handleResetToDefaults,
    validationErrors,
    isDiscountRatioDisabled,
    isDiscountValueDisabled,
    isSurchargeRatioDisabled,
    isSurchargeValueDisabled,
    loading,
  } = useProjectPriceSummaryDetails(props.priceSummary)

  const { handleChangeFilter, filterIsActive, resourceIdFilter } =
    useResourceIdFilter()

  const { articleIdFilter } = useArticleIdFilter()

  if (!props.priceSummary?.isWorkingStep) {
    return null
  }

  if (articleIdFilter.active) {
    return null
  }

  if (
    resourceIdFilter.active &&
    !resourceIdFilter.filter.enabledFilters.includes(
      props.priceSummary.workingStep.resource.id
    )
  ) {
    return null
  }

  return (
    <form
      className={classes.form}
      onSubmit={handleSubmit}
      onReset={handleReset}
    >
      <div
        id={
          props.priceSummary.materialId ||
          props.priceSummary.workingStep?.resource?.id
        }
        className={classes.priceSummaryGrid}
      >
        <div className={classes.priceSummaryDescription}>
          <div className={classes.name}>
            <WorkingStepIconSvg
              workingStepType={
                changedPriceSummary.workingStep.primaryWorkingStep
              }
            />
            <div>
              {props.priceSummary.workingStep.resource?.name}

              <Typography
                variant="body2"
                component="div"
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '1rem',
                  alignItems: 'baseline',
                }}
              >
                {`${getLocalizedWorkingStep(
                  props.priceSummary.workingStep.primaryWorkingStep
                )}`}
                {props.priceSummary.workingStep.primaryWorkingStep !==
                  props.priceSummary.workingStep.secondaryWorkingStep && (
                  <Typography className={classes.secondaryWorkingStepName}>
                    {`${getLocalizedWorkingStep(
                      props.priceSummary.workingStep.secondaryWorkingStep
                    )}`}
                  </Typography>
                )}
              </Typography>
            </div>
          </div>
        </div>

        <div className={classes.usage}>
          <Typography variant="caption">
            {TimeString(changedPriceSummary.productionTime)}
          </Typography>
        </div>
        <div className={classes.rate}>
          <MoneyPerQuantityInput
            label={t('project:rate-per-time-unit', 'rate per time unit')}
            value={changedPriceSummary.rate}
            name="rate"
            onChange={(e) => handleChange('rate', e.currentTarget.value)}
            defaultUnits={['kg', 'g']}
          />
          <HomePriceButtonOutlined
            handleResetToDefaults={() => handleResetToDefaults('costPrice')}
            isLoading={
              loading[getLoadingKey('delete-cost-price', changedPriceSummary)]
            }
            isManuallySet={changedPriceSummary.isCostPriceManuallySet}
          />
        </div>
        <div className={classes.filterButton}>
          <Tooltip
            title={t('project:filter-by-resource-id', 'filter by resource')}
          >
            <IconButton
              onClick={() =>
                handleChangeFilter(props.priceSummary.workingStep.resource.id)
              }
              style={{ alignSelf: 'center' }}
              size="small"
            >
              {filterIsActive(props.priceSummary.workingStep.resource.id) ? (
                <FilterAltOffOutlined />
              ) : (
                <FilterAltOutlined />
              )}
            </IconButton>
          </Tooltip>
        </div>
        <ValueLabel
          value={MoneyString(changedPriceSummary.productionTimeCosts)}
          label={'costs [prod time]'}
          labelTranslationKey={'project:costs-prod'}
          valueLabelRootStyles={{
            whiteSpace: 'pre-line',
            gridArea: 'costProd',
          }}
        />
        <ValueLabel
          label={'costs [additional]'}
          labelTranslationKey={'project:costs-additional'}
          value={MoneyString(changedPriceSummary.additionalCosts)}
          valueLabelRootStyles={{
            whiteSpace: 'pre-line',
            gridArea: 'costAdd',
          }}
        />
        <ValueLabel
          label={'costs [total]'}
          labelTranslationKey={'project:costs-total'}
          value={MoneyString(changedPriceSummary.totalCostPrice)}
          valueLabelRootStyles={{
            whiteSpace: 'pre-line',
            gridArea: 'cost',
          }}
        />
        {props.priceSummary.canEditDiscountOrSurcharge ? (
          <EditableFields
            changedPriceSummary={changedPriceSummary}
            handleChange={handleChange}
            handleResetToDefaults={handleResetToDefaults}
            validationErrors={validationErrors}
            isDiscountRatioDisabled={isDiscountRatioDisabled}
            isDiscountValueDisabled={isDiscountValueDisabled}
            isSurchargeRatioDisabled={isSurchargeRatioDisabled}
            isSurchargeValueDisabled={isSurchargeValueDisabled}
            loading={loading}
          />
        ) : (
          <ReadOnlyFields changedPriceSummary={changedPriceSummary} />
        )}
        <ValueLabel
          label={'sales price'}
          labelTranslationKey={'project:sales-price'}
          value={MoneyString(changedPriceSummary.totalSalesPrice)}
          valueLabelRootStyles={{
            gridArea: 'salesPrice',
          }}
        />
      </div>
      {changed && (
        <div className={classes.actions}>
          <ul>
            {Object.keys(validationErrors).map((errKey) => (
              <li key={errKey}>
                <Typography color="secondary" variant="caption">
                  {validationErrors[errKey]}
                </Typography>
              </li>
            ))}
          </ul>

          <Button
            size="small"
            type="reset"
            variant="outlined"
            color="secondary"
          >
            cancel
          </Button>
          <SaveButton
            disabled={Object.keys(validationErrors).length > 0}
            size="small"
            saving={saving}
          />
        </div>
      )}
    </form>
  )
}
